import { sql } from "drizzle-orm";
import {
  check,
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { activity } from "@/db/schema/activities";
import type { MediaData } from "@/db/schema/hiive";
import { taxProfile } from "@/db/schema/tax";
import { hiiveTier } from "@/db/schema/tier";
import { venue, venueAddon, venueSpot } from "@/db/schema/venue";
import type { PaymentCollection } from "@/lib/booking-collection";
import type { MusicAttachment } from "@/lib/music/attachment";
import type { PricingPolicy } from "@/lib/pricing/types";

// What is being booked. Three shapes cover all real-world cases:
//   - specific_spot:    customer picks a specific court/room/lane for N minutes
//                       (e.g. badminton court, private yoga studio, squash court)
//   - shared_session:   customer joins a class/session with capacity and a fixed
//                       start/end time (e.g. 7am yoga, 9am pilates, instructor-led
//                       bouldering session). Multiple customers share the session.
//   - entry_pass:       timed entry / open-session pass with capacity (e.g. open
//                       bouldering pass, walk-in gym session, diving pool entry).
//                       Customer picks a start window, stays for a duration.
export const BOOKING_TARGET = [
  "specific_spot",
  "shared_session",
  "entry_pass",
] as const;
export type BookingTarget = (typeof BOOKING_TARGET)[number];

// flexible_time: customer picks a start time on the N-minute grid and stays
//                for a multiple of that step (e.g. 30-min grid, 60/90/120 min).
//                Best for court rentals and private sessions.
// fixed_slots:   admin pre-declares slot templates (e.g. 7–8am, 9–10am).
//                Customer picks from that list. Best for classes/sessions.
export const BOOKING_MODE = ["flexible_time", "fixed_slots"] as const;
export type BookingMode = (typeof BOOKING_MODE)[number];

export const EXPERIENCE_STATUS = ["draft", "published", "archived"] as const;
export type ExperienceStatus = (typeof EXPERIENCE_STATUS)[number];

export const PRICE_UNIT = ["per_step", "per_slot"] as const;
export type PriceUnit = (typeof PRICE_UNIT)[number];

// The sellable unit. Flat sibling model — no parent/variant hierarchy. So
// "Badminton Singles 30 min", "Badminton Doubles 60 min", "Morning Yoga",
// "Open Bouldering Pass" are all siblings under a venue. Each carries its own
// pricing, schedule, addons, and allowed tiers.
export const venueExperience = sqliteTable(
  "venue_experience",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    venueId: integer("venue_id")
      .notNull()
      .references(() => venue.id, { onDelete: "cascade" }),

    activityId: text("activity_id")
      .notNull()
      .references(() => activity.id, { onDelete: "cascade" }),

    name: text("name").notNull(),
    slug: text("slug").notNull(),
    description: text("description").notNull().default(""),
    // Experience-level discovery gallery. Application validation and database
    // triggers require 1–6 items for every new experience and every publish.
    // The [] default is retained for legacy rows only.
    bannerMedia: text("banner_media", { mode: "json" })
      .$type<MediaData[]>()
      .notNull()
      .default(sql`'[]'`),
    /** Optional song for a static banner — see `event.bannerMusic`. */
    bannerMusic: text("banner_music", {
      mode: "json",
    }).$type<MusicAttachment | null>(),

    bookingTarget: text("booking_target")
      .notNull()
      .$type<BookingTarget>()
      .default("specific_spot"),

    // Per-experience label shown to customers when picking a spot.
    // "Court" for badminton, "Mat" for yoga, "Wall" for bouldering, etc.
    // Only meaningful for specific_spot. Null for shared_session / entry_pass.
    spotLabel: text("spot_label"),

    bookingMode: text("booking_mode")
      .notNull()
      .$type<BookingMode>()
      .default("flexible_time"),
    durationStepMinutes: integer("duration_step_minutes").notNull().default(30),
    minDurationMinutes: integer("min_duration_minutes").notNull().default(30),
    maxDurationMinutes: integer("max_duration_minutes").notNull().default(120),

    // For shared_session: total attendee capacity per session (seats in the
    // class). For entry_pass: concurrent capacity of the pool/gym. Null means
    // no explicit cap (engine falls back to number of mapped spots if any).
    capacity: integer("capacity"),

    paymentCollection: text("payment_collection")
      .notNull()
      .$type<PaymentCollection>()
      .default("online"),
    saleStartsAt: integer("sale_starts_at", { mode: "timestamp" }),
    saleEndsAt: integer("sale_ends_at", { mode: "timestamp" }),

    // Lead-time / window constraints. Minutes.
    minAdvanceBookingMinutes: integer("min_advance_booking_minutes")
      .notNull()
      .default(0),
    maxAdvanceBookingDays: integer("max_advance_booking_days")
      .notNull()
      .default(60),

    // Default price used when no pricing rule matches.
    // For flexible-time experiences this is typically per_step.
    // For fixed-slot experiences this is typically per_slot.
    basePriceMinor: integer("base_price_minor").notNull().default(0),
    basePriceUnit: text("base_price_unit")
      .notNull()
      .$type<PriceUnit>()
      .default("per_step"),
    currency: text("currency").notNull().default("INR"),

    // Tax. Held on the EXPERIENCE rather than on each ticket, because a venue
    // booking is one supply of one experience — its adult/child tickets are
    // variants of the same service and share its rate. That is why venues fold
    // once at the booking while events fold per line: an event basket can hold
    // an 18% workshop pass beside a 5% fitness pass, a venue booking cannot.
    // Null = untaxed, which is every row predating this.
    pricingPolicy: text("pricing_policy", {
      mode: "json",
    }).$type<PricingPolicy | null>(),
    taxProfileId: integer("tax_profile_id").references(() => taxProfile.id, {
      onDelete: "set null",
    }),
    priceIncludesTax: integer("price_includes_tax", { mode: "boolean" })
      .notNull()
      .default(true),

    sortOrder: integer("sort_order").notNull().default(0),
    status: text("status").notNull().$type<ExperienceStatus>().default("draft"),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("venue_experience_venue_slug_unique").on(t.venueId, t.slug),
    index("venue_experience_by_venue").on(t.venueId),
    index("venue_experience_by_status").on(t.venueId, t.status),
    index("venue_experience_sort_idx").on(t.venueId, t.sortOrder),
    check(
      "venue_experience_booking_mode_check",
      sql`${t.bookingMode} in ('flexible_time', 'fixed_slots')`,
    ),
    check(
      "venue_experience_duration_step_check",
      sql`${t.durationStepMinutes} >= 1 and ${t.durationStepMinutes} <= 1440`,
    ),
    check(
      "venue_experience_duration_step_multiple_of_15_check",
      sql`${t.durationStepMinutes} % 15 = 0`,
    ),
    check(
      "venue_experience_duration_order_check",
      sql`${t.minDurationMinutes} <= ${t.maxDurationMinutes}`,
    ),
    check(
      "venue_experience_min_duration_multiple_of_15_check",
      sql`${t.minDurationMinutes} % 15 = 0`,
    ),
    check(
      "venue_experience_min_duration_multiple_check",
      sql`${t.minDurationMinutes} % ${t.durationStepMinutes} = 0`,
    ),
    check(
      "venue_experience_max_duration_multiple_of_15_check",
      sql`${t.maxDurationMinutes} % 15 = 0`,
    ),
    check(
      "venue_experience_max_duration_multiple_check",
      sql`${t.maxDurationMinutes} % ${t.durationStepMinutes} = 0`,
    ),
    check(
      "venue_experience_capacity_check",
      sql`${t.capacity} is null or ${t.capacity} >= 1`,
    ),
    check("venue_experience_base_price_check", sql`${t.basePriceMinor} >= 0`),
    index("venue_experience_activity_idx").on(t.activityId),
  ],
);

// Which physical spots can fulfil this experience. Only meaningful when
// bookingTarget = specific_spot (customer picks one of these) or when spot
// pooling is used for entry_pass capacity tracking.
//
// `basePriceMinor` is an optional per-spot override of the experience's
// base price. When the customer picks this spot and no pricing rule matches,
// the engine charges this amount. When null, the experience-level base price
// applies. Only meaningful for specific_spot bookings.
export const venueExperienceSpot = sqliteTable(
  "venue_experience_spot",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    experienceId: integer("experience_id")
      .notNull()
      .references(() => venueExperience.id, { onDelete: "cascade" }),
    spotId: integer("spot_id")
      .notNull()
      .references(() => venueSpot.id, { onDelete: "cascade" }),
    sortOrder: integer("sort_order").notNull().default(0),
    basePriceMinor: integer("base_price_minor"),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("venue_experience_spot_unique").on(t.experienceId, t.spotId),
    index("venue_experience_spot_by_experience").on(t.experienceId),
    index("venue_experience_spot_by_spot").on(t.spotId),
    check(
      "venue_experience_spot_base_price_check",
      sql`${t.basePriceMinor} is null or ${t.basePriceMinor} >= 0`,
    ),
  ],
);

// Sellable ticket/pass types for entry_pass experiences. Capacity is not stored
// here; timed access and day-pass capacity belongs to the schedule/fixed slot
// that the customer selects. These rows define the customer-facing pass choices
// that share that slot capacity.
export const venueExperienceTicket = sqliteTable(
  "venue_experience_ticket",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    experienceId: integer("experience_id")
      .notNull()
      .references(() => venueExperience.id, { onDelete: "cascade" }),

    name: text("name").notNull(),
    slug: text("slug").notNull(),
    description: text("description").notNull().default(""),

    basePriceMinor: integer("base_price_minor").notNull().default(0),
    maxPerBooking: integer("max_per_booking"),

    sortOrder: integer("sort_order").notNull().default(0),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("venue_experience_ticket_slug_unique").on(
      t.experienceId,
      t.slug,
    ),
    index("venue_experience_ticket_by_experience").on(t.experienceId),
    index("venue_experience_ticket_sort_idx").on(t.experienceId, t.sortOrder),
    check(
      "venue_experience_ticket_base_price_check",
      sql`${t.basePriceMinor} >= 0`,
    ),
    check(
      "venue_experience_ticket_max_per_booking_check",
      sql`${t.maxPerBooking} is null or ${t.maxPerBooking} >= 1`,
    ),
  ],
);

// Which addons may be offered with this experience.
export const venueExperienceAddon = sqliteTable(
  "venue_experience_addon",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    experienceId: integer("experience_id")
      .notNull()
      .references(() => venueExperience.id, { onDelete: "cascade" }),
    addonId: integer("addon_id")
      .notNull()
      .references(() => venueAddon.id, { onDelete: "cascade" }),
    isRequired: integer("is_required", { mode: "boolean" })
      .notNull()
      .default(false),
    // Pre-selected but removable: starts in the basket at quantity 1, unlike
    // isRequired the customer may take it back out (min 0). Presentation-only;
    // the booking engine never enforces it.
    isPreselected: integer("is_preselected", { mode: "boolean" })
      .notNull()
      .default(false),
    sortOrder: integer("sort_order").notNull().default(0),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("venue_experience_addon_unique").on(t.experienceId, t.addonId),
    index("venue_experience_addon_by_experience").on(t.experienceId),
  ],
);

// Access control — which hiive tiers may book this experience. If no rows
// exist for an experience, it's open to everyone.
export const venueExperienceAllowedTier = sqliteTable(
  "venue_experience_allowed_tier",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    experienceId: integer("experience_id")
      .notNull()
      .references(() => venueExperience.id, { onDelete: "cascade" }),
    tierId: integer("tier_id")
      .notNull()
      .references(() => hiiveTier.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("venue_experience_allowed_tier_unique").on(
      t.experienceId,
      t.tierId,
    ),
    index("venue_experience_allowed_tier_by_experience").on(t.experienceId),
    index("venue_experience_allowed_tier_by_tier").on(t.tierId),
  ],
);

// Weekly recurring availability window. (dayOfWeek: 0=Sun..6=Sat, minutes
// since 00:00)
// For flexible-time experiences: slot grid is laid out inside these windows.
// For fixed-slot experiences: not used (use venueExperienceFixedSlot instead).
export const venueExperienceSchedule = sqliteTable(
  "venue_experience_schedule",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    experienceId: integer("experience_id")
      .notNull()
      .references(() => venueExperience.id, { onDelete: "cascade" }),
    dayOfWeek: integer("day_of_week").notNull(),
    spotId: integer("spot_id").references(() => venueSpot.id, {
      onDelete: "cascade",
    }),
    startMinute: integer("start_minute").notNull(),
    endMinute: integer("end_minute").notNull(),
    capacity: integer("capacity"),
    effectiveStartDate: text("effective_start_date"),
    effectiveEndDate: text("effective_end_date"),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),
    ...timestamps,
  },
  (t) => [
    index("venue_experience_schedule_by_experience").on(t.experienceId),
    index("venue_experience_schedule_by_dow").on(t.experienceId, t.dayOfWeek),
    index("venue_experience_schedule_by_spot").on(t.spotId),
    check(
      "venue_experience_schedule_day_check",
      sql`${t.dayOfWeek} >= 0 and ${t.dayOfWeek} <= 6`,
    ),
    check(
      "venue_experience_schedule_minute_order_check",
      sql`${t.startMinute} >= 0 and ${t.startMinute} < ${t.endMinute} and ${t.endMinute} <= 1440`,
    ),
  ],
);

// For fixed-slot (shared_session) experiences — pre-declared slot templates.
// Customer picks from the generated list. Capacity override lets e.g. the 7am
// yoga class have 20 seats while the 5pm advanced class has 8.
export const venueExperienceFixedSlot = sqliteTable(
  "venue_experience_fixed_slot",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    experienceId: integer("experience_id")
      .notNull()
      .references(() => venueExperience.id, { onDelete: "cascade" }),
    dayOfWeek: integer("day_of_week").notNull(),
    spotId: integer("spot_id").references(() => venueSpot.id, {
      onDelete: "cascade",
    }),
    startMinute: integer("start_minute").notNull(),
    durationMinutes: integer("duration_minutes").notNull(),
    capacity: integer("capacity"),
    priceMinor: integer("price_minor"),
    // Optional customer-facing context (for example an instructor or class
    // variant). Leave null when the date and time already identify the slot.
    label: text("label"),
    effectiveStartDate: text("effective_start_date"),
    effectiveEndDate: text("effective_end_date"),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),
    ...timestamps,
  },
  (t) => [
    index("venue_experience_fixed_slot_by_experience").on(t.experienceId),
    index("venue_experience_fixed_slot_by_dow").on(t.experienceId, t.dayOfWeek),
    index("venue_experience_fixed_slot_by_spot").on(t.spotId),
    check(
      "venue_experience_fixed_slot_day_check",
      sql`${t.dayOfWeek} >= 0 and ${t.dayOfWeek} <= 6`,
    ),
    check(
      "venue_experience_fixed_slot_start_check",
      sql`${t.startMinute} >= 0 and ${t.startMinute} <= 1439`,
    ),
    check(
      "venue_experience_fixed_slot_duration_check",
      sql`${t.durationMinutes} >= 1 and (${t.startMinute} + ${t.durationMinutes}) <= 1440`,
    ),
    check(
      "venue_experience_fixed_slot_capacity_check",
      sql`${t.capacity} is null or ${t.capacity} >= 1`,
    ),
  ],
);

// Flat ordered price rules. First matching rule (by ascending sortOrder) wins.
// Resolution order when no rule matches: per-spot base (venueExperienceSpot.
// basePriceMinor) → experience.basePriceMinor.
//
// A rule matches when every non-null filter it carries is satisfied:
//   - spotId: applies only when the customer picked this spot (specific_spot)
//   - dayOfWeek, startMinute/endMinute: applies if the slot starts inside window
//   - validFrom/validTo: applies if the slot starts inside the date range
//   - tierId: applies only if the booking customer is in this tier
// Every null filter is a wildcard. Lower sortOrder = higher precedence (0 is
// evaluated first; use large numbers for fallback "default" rules).
export const venueExperiencePriceRule = sqliteTable(
  "venue_experience_price_rule",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    experienceId: integer("experience_id")
      .notNull()
      .references(() => venueExperience.id, { onDelete: "cascade" }),

    name: text("name").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),

    spotId: integer("spot_id").references(() => venueSpot.id, {
      onDelete: "cascade",
    }),
    ticketId: integer("ticket_id").references(() => venueExperienceTicket.id, {
      onDelete: "cascade",
    }),
    tierId: integer("tier_id").references(() => hiiveTier.id, {
      onDelete: "cascade",
    }),
    dayOfWeek: integer("day_of_week"),
    startMinute: integer("start_minute"),
    endMinute: integer("end_minute"),
    validFrom: integer("valid_from", { mode: "timestamp" }),
    validTo: integer("valid_to", { mode: "timestamp" }),

    priceUnit: text("price_unit")
      .notNull()
      .$type<PriceUnit>()
      .default("per_step"),
    priceAmountMinor: integer("price_amount_minor").notNull(),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),
    ...timestamps,
  },
  (t) => [
    index("venue_experience_price_rule_by_experience").on(t.experienceId),
    index("venue_experience_price_rule_by_active").on(
      t.experienceId,
      t.isActive,
    ),
    index("venue_experience_price_rule_by_sort_order").on(
      t.experienceId,
      t.sortOrder,
    ),
    index("venue_experience_price_rule_by_spot").on(t.spotId),
    index("venue_experience_price_rule_by_ticket").on(t.ticketId),
    index("venue_experience_price_rule_by_tier").on(t.tierId),
    check(
      "venue_experience_price_rule_day_check",
      sql`${t.dayOfWeek} is null or (${t.dayOfWeek} >= 0 and ${t.dayOfWeek} <= 6)`,
    ),
    check(
      "venue_experience_price_rule_window_check",
      sql`(${t.startMinute} is null and ${t.endMinute} is null) or (${t.startMinute} is not null and ${t.endMinute} is not null and ${t.startMinute} < ${t.endMinute})`,
    ),
    check(
      "venue_experience_price_rule_validity_check",
      sql`${t.validFrom} is null or ${t.validTo} is null or ${t.validFrom} < ${t.validTo}`,
    ),
    check(
      "venue_experience_price_rule_amount_check",
      sql`${t.priceAmountMinor} >= 0`,
    ),
  ],
);

export type VenueExperience = typeof venueExperience.$inferSelect;
export type VenueExperienceInsert = typeof venueExperience.$inferInsert;
export type VenueExperienceSpot = typeof venueExperienceSpot.$inferSelect;
export type VenueExperienceSpotInsert = typeof venueExperienceSpot.$inferInsert;
export type VenueExperienceTicket = typeof venueExperienceTicket.$inferSelect;
export type VenueExperienceTicketInsert =
  typeof venueExperienceTicket.$inferInsert;
export type VenueExperienceAddon = typeof venueExperienceAddon.$inferSelect;
export type VenueExperienceAddonInsert =
  typeof venueExperienceAddon.$inferInsert;
export type VenueExperienceAllowedTier =
  typeof venueExperienceAllowedTier.$inferSelect;
export type VenueExperienceAllowedTierInsert =
  typeof venueExperienceAllowedTier.$inferInsert;
export type VenueExperienceSchedule =
  typeof venueExperienceSchedule.$inferSelect;
export type VenueExperienceScheduleInsert =
  typeof venueExperienceSchedule.$inferInsert;
export type VenueExperienceFixedSlot =
  typeof venueExperienceFixedSlot.$inferSelect;
export type VenueExperienceFixedSlotInsert =
  typeof venueExperienceFixedSlot.$inferInsert;
export type VenueExperiencePriceRule =
  typeof venueExperiencePriceRule.$inferSelect;
export type VenueExperiencePriceRuleInsert =
  typeof venueExperiencePriceRule.$inferInsert;
